import React, { useEffect, useState, useContext } from 'react';
import { cloneDeep } from 'lodash';
import { ChartData, Notifier, notify } from '@scholastic/volume-react';
import ReportChartHeader from './ReportChartHeader/ReportChartHeader';
import ReportChartBody from './ReportChartBody/ReportChartBody';
import { fetchChartData } from '../../state/ReportsChart.api';
import {
  TransformChartDataResponse,
  ChartApiRequest,
  ChartApiResponse,
  transformChartData,
  transformIndividualChartData,
  transformGroupedChartData,
} from './ReportDataTransform';
import './ReportCharts.scss';

import { StoreContext } from '../../state/store';

enum SelectdTab {
  totalUsages = 'Total Usage',
  remoteUsages = 'Remote Usage',
  onsiteUsages = 'Onsite Usage',
  individualUsages = 'Individual Usage',
}

const ReportCharts = () => {
  const { state } = useContext(StoreContext);

  const reportParams = state.reportInfo || state.groupReportInfo;
  const [formattedData, setFormattedData] = useState<TransformChartDataResponse | null>(null);

  const [selectedTab, setSelectedTab] = useState<string>(SelectdTab.totalUsages);
  const [exportedData, setExportedData] = useState<ChartData>();

  const [beginingDate, setBeginingDate] = useState(reportParams?.startDate);
  const [lastDate, setLastDate] = useState(reportParams?.endDate);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);

  const groupSize: number = 50;
  const [page, setPage] = useState(0);
  const totalPages = reportParams?.groups
    ? Math.ceil(Object.keys(reportParams?.groups).length / groupSize)
    : 1;

  const returnFilteredGroups = async () => {
    if (reportParams?.groups) {
      return Object.keys(reportParams?.groups)
        .slice(page * groupSize, page * groupSize + groupSize)
        .reduce((result: any, key) => {
          result[key] = reportParams?.groups[key];

          return result;
        }, {});
    } else {
      return undefined;
    }
  };
  useEffect(() => {
    //single api call with new date range and fetch the chart data
    const loadChartData = async (): Promise<void> => {
      const filteredGroups = await returnFilteredGroups();
      const requestObj: ChartApiRequest = {
        request: reportParams?.reportType,
        cuid: reportParams?.cuId,
        startDate: beginingDate?.toLocaleDateString('fr-CA'),
        endDate: lastDate?.toLocaleDateString('fr-CA'),
        product: reportParams?.product,
        customer: reportParams?.customer,
        groups: filteredGroups,
        byProduct: reportParams?.byProduct,
        valueKey: reportParams?.valueKey,
      };

      try {
        const rawCharttData: ChartApiResponse = await fetchChartData(requestObj);

        const formattedChartData: TransformChartDataResponse =
          reportParams?.reportType === 'documentUsageByGroup' ||
          reportParams?.reportType === 'sessionUsageByGroup'
            ? transformGroupedChartData(rawCharttData)
            : transformChartData(
                rawCharttData,
                reportParams?.reportType,
                reportParams?.productName,
              );

        setFormattedData(formattedChartData);
        setDataLoaded(true);
      } catch (err) {
        notify.warning('Something went wrong, please try again later');
        console.error(err);
      }
    };

    // Multiple api call for individual site report
    const loadChartDataForIndividualReport = async (): Promise<void> => {
      if (
        reportParams.institutions &&
        reportParams.product &&
        reportParams.institutions.length > 0
      ) {
        for (let i = 0; i < reportParams.product.length; i++) {
          let productChartData: any[] = [];
          let batchPromises: any[] = [];

          for (let j = 0; j < reportParams.institutions.length; j++) {
            let institution = reportParams.institutions[j];
            batchPromises.push(
              fetchChartData({
                request: reportParams?.reportType,
                cuid: institution.value,
                startDate: beginingDate?.toLocaleDateString('fr-CA'),
                endDate: lastDate?.toLocaleDateString('fr-CA'),
                product: reportParams?.product,
                customer: institution.value,
              }).then((value) => ({
                institution: institution.label,
                ...value,
              })),
            );

            // Sends 10 Request at the same time.
            if (batchPromises.length === 10 || j === reportParams.institutions.length - 1) {
              const batchValues = await Promise.all(batchPromises);
              productChartData = productChartData.concat(batchValues);
              batchPromises = [];
            }
          }

          const formattedChartData: TransformChartDataResponse =
            transformIndividualChartData(productChartData);

          setFormattedData(formattedChartData);
          setDataLoaded(true);
        }
      }
    };
    setDataLoaded(false);
    setFormattedData(null);
    if (reportParams && reportParams.institutions) {
      try {
        loadChartDataForIndividualReport();
      } catch (err) {
        notify.warning('Something went wrong, please try again later');
        console.error(err);
      }
    } else {
      loadChartData();
    }

    // eslint-disable-next-line
  }, [beginingDate, lastDate, page]);

  useEffect(() => {
    const deriveCsvData = (): void => {
      if (selectedTab === SelectdTab.totalUsages) {
        setExportedData(formattedData?.totalChartobj);
      } else if (selectedTab === SelectdTab.remoteUsages) {
        setExportedData(formattedData?.remoteChartObj);
      } else if (selectedTab === SelectdTab.onsiteUsages) {
        setExportedData(formattedData?.onsiteChartObj);
      } else if (selectedTab === SelectdTab.individualUsages) {
        setExportedData(formattedData?.individualChartObj);
      }
    };

    deriveCsvData();
  }, [selectedTab, formattedData]);

  const fetchPreviousData = (): void => {
    const chartStartDate = beginingDate;
    chartStartDate.setMonth(chartStartDate.getMonth() - 6);
    chartStartDate.setDate(1);

    const chartEndDate = cloneDeep(chartStartDate);
    chartEndDate.setMonth(chartEndDate.getMonth() + 14);
    chartEndDate.setDate(1);

    setBeginingDate(chartStartDate);
    setLastDate(chartEndDate);
  };

  const fetchNextData = (): void => {
    const chartEndDateNxt = lastDate;
    chartEndDateNxt.setMonth(chartEndDateNxt.getMonth() + 6);
    chartEndDateNxt.setDate(1);

    const chartStartDateNxt = cloneDeep(chartEndDateNxt);
    chartStartDateNxt.setMonth(chartStartDateNxt.getMonth() - 14);
    chartStartDateNxt.setDate(1);

    setLastDate(chartEndDateNxt);
    setBeginingDate(chartStartDateNxt);
  };

  return (
    <div className="stats-report-charts">
      <Notifier
        autoClose={5000}
        closeButton={false}
        closeOnClick={false}
        draggable
        draggablePercent={50}
        limit={1}
        newestOnTop
        pauseOnFocusLoss
        pauseOnHover
        position="top-center"
        role="alert"
      />

      <div className="stats-report-charts__header">
        <ReportChartHeader
          reportType={reportParams?.reportType}
          productName={reportParams?.productName}
          csvData={exportedData}
          selectedTab={selectedTab}
        />
      </div>

      <div className="stats-report-charts__body mt-5">
        <ReportChartBody
          loggedInUserName={reportParams?.loggedInUserName}
          productName={reportParams?.productName}
          reportType={reportParams?.reportType}
          chartData={formattedData}
          onPreviousClick={fetchPreviousData}
          onNextClick={fetchNextData}
          loadMore={(pageNo) => setPage(pageNo)}
          page={page}
          totalPages={totalPages}
          dataLoaded={dataLoaded}
          maxPageSize={groupSize}
          selectedTab={setSelectedTab}
        />
      </div>

      <div className="stats-report-charts__footer"></div>
    </div>
  );
};

export default ReportCharts;
